import { Button } from '@efexteam/components';
import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@mui/material';
import useGetPageWidth from '@pages/requestTerms/hooks/useGetPageWidth';
import { title } from 'process';
import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import { useSelector } from 'react-redux';
import useBoolean from '../../hooks/useBoolean';
import useImmediateApiCall from '../../hooks/useImmediateApiCall';
import ErrorState from '../../modules/payments/ErrorState';
import LoaderInvoice from '../../modules/payments/LoaderInvoice';
import clientSvc from '../../services/client/clientSvc';
import {
  IPaymentDetail,
  PaymentDetailResponse,
} from '../../services/client/types';
import { useAppDispatch } from '../../store';
import { camelCaseMapData } from '../../utils/formater';
import { fetchUserProfile } from '../profile/store';
import { selectBusiness } from '../profile/store/selectors';
import { InvoiceTypes, URI } from './config';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url
).toString();

interface IPaymentProps {
  origin: InvoiceTypes;
  identify: string;
  paymentId: number;
  onClose: () => void;
}

const Payment = ({ origin, identify, onClose, paymentId }: IPaymentProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const company = useSelector(selectBusiness);
  const { pageWidth } = useGetPageWidth('#paymentWrapper');

  const [
    isDownloadingPdf,
    { setTrue: showIsDownloadingPdf, setFalse: hideIsDownloadingPdf },
  ] = useBoolean(false);

  const { data, error, loading } = useImmediateApiCall(() =>
    clientSvc.getPaymentReceipt({
      identify,
      enterpriseId: company?.enterpriseId ?? '',
      language: i18n.language,
    })
  );

  const { data: oldData } = useImmediateApiCall(() =>
    clientSvc
      .getPaymentDetail({ identify, origin })
      .then((response) =>
        camelCaseMapData<PaymentDetailResponse, IPaymentDetail>(response)
      )
  );

  const handleDownloadPDF = useCallback(async () => {
    const linkSource = `data:application/pdf;base64,${data}`;
    const downloadLink = document.createElement('a');
    const fileName = `Transfer_receipt_${paymentId}.pdf`;

    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
    hideIsDownloadingPdf();
  }, [data, hideIsDownloadingPdf, paymentId]);

  const handleDownloadCEP = async () => {
    window.open(oldData.cep, '_blank');
  };

  const handleDownloadInvoice = (invoicesArray: string[]) => {
    invoicesArray.forEach((invoiceUrl) => {
      const anchor = document.createElement('a');
      anchor.target = '_blank';
      document.body.appendChild(anchor);
      const filePath = `${URI}${invoiceUrl}`;
      const nameBase = filePath.split('/');
      const name = nameBase[nameBase.length - 1];
      anchor.href = filePath;
      anchor.download = name;
      anchor.click();
      document.body.removeChild(anchor);
    });
  };

  useEffect(() => {
    if (!company) dispatch(fetchUserProfile());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  useEffect(() => {
    if (isDownloadingPdf) handleDownloadPDF();
  }, [isDownloadingPdf, handleDownloadPDF]);

  if (error || data?.message === 'Data Empty') {
    return (
      <Box className="!max-w-[600px] !lg:w-full mx-12 py-[50px] flex justify-center items-center h-full">
        <ErrorState showBackButton={false} close={onClose} />
      </Box>
    );
  }

  if (loading || !data || !title) {
    return (
      <Box className="!max-w-[600px] !lg:w-full mx-12 py-[50px] flex justify-center items-center h-full">
        <LoaderInvoice />
      </Box>
    );
  }

  return (
    <Box className="relative font-sans" id="invoice_print">
      <Box
        className={`!max-w-[600px] w-[100vw] px-3 py-[50px]  ${
          isDownloadingPdf ? 'pt-[10%]' : ''
        }`}
      >
        {!isDownloadingPdf && (
          <button
            aria-label="Close"
            type="button"
            className="ml-auto block mb-3"
            id="id_download"
            onClick={() => onClose()}
          >
            <CloseIcon />
          </button>
        )}
        <Document
          file={`data:application/pdf;base64,${data}`}
          onLoadError={console.error}
        >
          <Page renderTextLayer={false} pageNumber={1} width={pageWidth - 20} />
        </Document>
        <Box className="flex flex-wrap my-5 justify-center gap-3">
          {!isDownloadingPdf && (
            <Button
              appearance="primary"
              className="px-3 py-2 bg-secondary-text text-white hover:bg-primary-text rounded-[3px] w-full md:w-auto"
              id="id_download"
              onClick={() => showIsDownloadingPdf()}
            >
              {t('invoiceDownloadBtn')}
            </Button>
          )}
          {!isDownloadingPdf && oldData?.cep && (
            <Button
              appearance="secondary"
              aria-label="Download CEP"
              type="button"
              className="px-3 py-2 border-solid border border-primary-text text-primary-text hover:bg-primary-text hover:text-white rounded-[3px]"
              id="id_download"
              onClick={() => handleDownloadCEP()}
            >
              {t('invoiceDownloadCepBtn')}
            </Button>
          )}
        </Box>
        <Box className="flex flex-wrap mt-3 justify-center">
          {!isDownloadingPdf &&
            oldData?.invoiceFiles &&
            oldData?.invoiceFiles?.length > 0 && (
              <Button
                appearance="secondary"
                aria-label="Download Invoice Files"
                type="button"
                id="id_download"
                className="px-3 py-2 border-solid border border-secondary-text text-secondary-text hover:bg-secondary-text hover:text-white rounded-[3px] w-full md:w-auto"
                onClick={() =>
                  handleDownloadInvoice(oldData?.invoiceFiles || [])
                }
              >
                {t('invoiceAttached')}
              </Button>
            )}
        </Box>
      </Box>
    </Box>
  );
};

export default Payment;
